import { useState, useMemo, useRef, useEffect } from 'react';
import { CheckCircle, Loader2, X, XCircle, Search, User, Phone, AlertTriangle, Euro, Radio } from 'lucide-react';
import type { Cliente, CreateReservaRequest, Mesa, Reserva, Canal } from '../types';
import { canaisApi } from '../services/api';
import { toLocalDateTimeInput } from '../utils/mesaEstado';

interface ReservaModalProps {
  title: string;
  formData: CreateReservaRequest;
  formErrors: Partial<Record<keyof CreateReservaRequest, string>>;
  clientes: Cliente[];
  mesas: Mesa[];
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onChange: React.Dispatch<React.SetStateAction<CreateReservaRequest>>;
  loading: boolean;
  reservaEditando: Reserva | null;
  onConfirmar?: (reserva: Reserva) => void;
  onCancelar?: (reserva: Reserva) => void;
  onEditar?: (reserva: Reserva) => void;
}

export function ReservaModal({
  title,
  formData,
  formErrors,
  clientes,
  mesas,
  onClose,
  onSubmit,
  onChange,
  loading,
  reservaEditando,
  onConfirmar,
  onCancelar,
}: ReservaModalProps) {
  const [clienteQuery, setClienteQuery] = useState('');
  const [showClienteList, setShowClienteList] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [canais, setCanais] = useState<Canal[]>([]);
  const [minDateTime] = useState(() => toLocalDateTimeInput(new Date()));
  useEffect(() => {
    canaisApi.getAll()
      .then(r => { if (r.success && r.data) setCanais(r.data.filter(c => c.ativo)); })
      .catch(() => {
        // silencioso: canal fica em "Auto (default)"
      });
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowClienteList(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Filter clients by nome OR telefone
  const filteredClientes = useMemo(() => {
    const q = clienteQuery.trim().toLowerCase();
    if (!q) return clientes.slice(0, 8);
    return clientes
      .filter(c => c.nome.toLowerCase().includes(q) || (c.telefone || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [clienteQuery, clientes]);

  // Detect phone duplicate
  const telefoneAviso = useMemo(() => {
    const tel = (formData.contatoReserva || '').trim();
    if (!tel || formData.clienteId) return null;
    const clean = tel.replace(/\s+/g, '');
    if (clean.length < 6) return null;
    return clientes.find(c => (c.telefone || '').replace(/\s+/g, '') === clean) || null;
  }, [formData.contatoReserva, formData.clienteId, clientes]);

  // Suggest when typing name but no cliente selected
  const nomeSugestoes = useMemo(() => {
    if (formData.clienteId) return [];
    const nome = (formData.nomeCliente || '').trim().toLowerCase();
    if (nome.length < 2) return [];
    return clientes.filter(c => c.nome.toLowerCase().includes(nome) && c.nome.toLowerCase() !== nome).slice(0, 4);
  }, [formData.nomeCliente, formData.clienteId, clientes]);

  // Group mesas by local for simple select (ordenado; evita re-sort no render)
  const mesasByLocal = useMemo(() => {
    const grouped = new Map<number, { localId: number; localDescricao: string; localCor: string; mesas: Mesa[] }>();
    mesas.forEach(m => {
      if (!grouped.has(m.localId)) {
        grouped.set(m.localId, { localId: m.localId, localDescricao: m.localDescricao, localCor: m.localCor, mesas: [] });
      }
      grouped.get(m.localId)!.mesas.push(m);
    });
    return Array.from(grouped.values())
      .map(g => ({ ...g, mesas: [...g.mesas].sort((a, b) => a.numero - b.numero) }))
      .sort((a, b) => a.localDescricao.localeCompare(b.localDescricao));
  }, [mesas]);

  const handlePickCliente = (cliente: Cliente) => {
    onChange(prev => ({
      ...prev,
      clienteId: cliente.id,
      nomeCliente: cliente.nome,
      contatoReserva: cliente.telefone || prev.contatoReserva || '',
    }));
    setClienteQuery('');
    setShowClienteList(false);
  };

  const handleClearCliente = () => {
    onChange(prev => ({ ...prev, clienteId: undefined }));
    setClienteQuery('');
  };

  const clienteSelecionado = formData.clienteId ? clientes.find(c => c.id === formData.clienteId) : null;
  const mesaSelecionadaObj = formData.mesaId ? mesas.find(m => m.id === formData.mesaId) : null;

  const handleClearMesa = () => {
    onChange(prev => ({ ...prev, mesaId: undefined }));
  };

  // Modal de formulário com dados não gravados: NÃO fecha com clique fora
  // (evita perder dados ao arrastar seleção de texto ou ao dispensar
  // pickers nativos como <select> e datetime-local). Fecha via X/Cancelar/Escape.
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div className="modal">
        <header className="modal-header">
          <h2 id="modal-title" className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Fechar" type="button">
            <X className="w-5 h-5" />
          </button>
        </header>

        <form id="reserva-form" onSubmit={onSubmit} className="modal-body" noValidate>
          {/* Cliente picker - pesquisável por nome e telefone */}
          <div className="form-group" ref={wrapperRef}>
            <label className="label">Cliente existente</label>
            {clienteSelecionado ? (
              <div className="cliente-picked">
                <div className="cliente-picked-info">
                  <span className="cliente-picked-name"><User className="w-4 h-4" />{clienteSelecionado.nome}</span>
                  {clienteSelecionado.telefone && <span className="cliente-picked-phone"><Phone className="w-4 h-4" />{clienteSelecionado.telefone}</span>}
                </div>
                <button type="button" className="btn btn-ghost btn-sm" onClick={handleClearCliente} disabled={!!reservaEditando}>Trocar</button>
              </div>
            ) : (
              <div className="combobox">
                <div className="combobox-input-wrap">
                  <Search className="combobox-icon" />
                  <input
                    type="text"
                    className="input combobox-input"
                    placeholder="Pesquisar por nome ou telefone..."
                    value={clienteQuery}
                    onChange={e => { setClienteQuery(e.target.value); setShowClienteList(true); }}
                    onFocus={() => setShowClienteList(true)}
                    disabled={!!reservaEditando}
                  />
                </div>
                {showClienteList && (
                  <ul className="combobox-list">
                    {filteredClientes.length === 0 ? (
                      <li className="combobox-empty">
                        Nenhum cliente encontrado
                        {clienteQuery.trim() && <span> — será criada nova ficha para &ldquo;{clienteQuery.trim()}&rdquo;</span>}
                      </li>
                    ) : (
                      filteredClientes.map(c => (
                        <li key={c.id}>
                          <button type="button" className="combobox-option" onClick={() => handlePickCliente(c)}>
                            <span className="combobox-option-name">{c.nome}</span>
                            <span className="combobox-option-phone">{c.telefone || '—'}</span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </div>
            )}
            <p className="form-hint">Pesquise por nome ou número. Se não escolher, será criada ficha automaticamente.</p>
          </div>

          <div className="form-group">
            <label htmlFor="nomeCliente" className="label">Nome do cliente <span className="required">*</span></label>
            <input
              type="text"
              id="nomeCliente"
              className={`input ${formErrors.nomeCliente ? 'input-error' : ''}`}
              value={formData.nomeCliente || ''}
              onChange={e => onChange(prev => ({ ...prev, nomeCliente: e.target.value }))}
              placeholder="Nome do cliente"
              required={!formData.clienteId}
            />
            {formErrors.nomeCliente && <p className="error-text">{formErrors.nomeCliente}</p>}
            {nomeSugestoes.length > 0 && (
              <div className="suggestion-row">
                <span className="suggestion-label">Sugestões:</span>
                {nomeSugestoes.map(c => (
                  <button key={c.id} type="button" className="suggestion-chip" onClick={() => handlePickCliente(c)}>
                    {c.nome} {c.telefone ? `· ${c.telefone}` : ''}
                  </button>
                ))}
              </div>
            )}
          </div>

          {reservaEditando && (
            <div className="form-group" style={{ background: 'var(--c-surface-2)', borderRadius: 8, padding: 10, border: '1px solid var(--c-border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>Criado por:</span>
                <strong style={{ fontSize: 13 }}>{reservaEditando.criadoPorNome || '—'}</strong>
                <span style={{ fontSize: 11, color: 'var(--c-text-muted)', marginLeft: 8 }}>#{reservaEditando.id} · v{reservaEditando.versao}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>Editado por:</span>
                <strong style={{ fontSize: 13 }}>{reservaEditando.editadoPorNome || '—'}</strong>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="contatoReserva" className="label">Telefone / Contacto</label>
              <input
                type="tel"
                id="contatoReserva"
                className={`input ${telefoneAviso ? 'input-warning' : ''}`}
                value={formData.contatoReserva || ''}
                onChange={e => onChange(prev => ({ ...prev, contatoReserva: e.target.value }))}
                placeholder="9xxxxxxxx"
              />
              {telefoneAviso && (
                <div className="inline-warning">
                  <AlertTriangle className="w-4 h-4" />
                  <span>Telefone já existe: <strong>{telefoneAviso.nome}</strong> ({telefoneAviso.telefone}).</span>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => handlePickCliente(telefoneAviso)}>Usar esta ficha</button>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="label">Mesa</label>
              {mesaSelecionadaObj ? (
                <div className="cliente-picked">
                  <div className="cliente-picked-info">
                    <span className="cliente-picked-name">{mesaSelecionadaObj.localDescricao} — Mesa {mesaSelecionadaObj.numero}</span>
                    <span className="cliente-picked-phone">{mesaSelecionadaObj.descricao} · {mesaSelecionadaObj.capacidadePessoas} pax</span>
                  </div>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={handleClearMesa}>Trocar</button>
                </div>
              ) : (
                <select
                  className="input"
                  value={formData.mesaId ?? ''}
                  onChange={e => {
                    const newMesaId = e.target.value ? Number(e.target.value) : undefined;
                    const mesa = mesas.find(m => m.id === newMesaId);
                    onChange(prev => ({
                      ...prev,
                      mesaId: newMesaId,
                      // sugere lotação da mesa se ainda está com valor default (2) ou vazio
                      numeroPessoas: mesa && (prev.numeroPessoas === 2 || !prev.numeroPessoas) ? mesa.capacidadePessoas : prev.numeroPessoas,
                    }));
                  }}
                >
                  <option value="">Sem mesa (mesa default)</option>
                  {mesasByLocal.map(local => (
                    <optgroup key={local.localId} label={local.localDescricao}>
                      {local.mesas
                        .map(m => (
                          <option key={m.id} value={m.id}>
                            Mesa {m.numero} — {m.descricao} ({m.capacidadePessoas} pax)
                          </option>
                        ))}
                    </optgroup>
                  ))}
                </select>
              )}
              <p className="form-hint">Selecione a mesa. Deixe vazio para mesa default.</p>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dataDaReserva" className="label">Data e hora <span className="required">*</span></label>
              <input
                type="datetime-local"
                id="dataDaReserva"
                className={`input ${formErrors.dataDaReserva ? 'input-error' : ''}`}
                value={formData.dataDaReserva}
                onChange={e => onChange(prev => ({ ...prev, dataDaReserva: e.target.value }))}
                required
                min={minDateTime}
              />
              {formErrors.dataDaReserva && <p className="error-text">{formErrors.dataDaReserva}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="numeroPessoas" className="label">Nº pessoas <span className="required">*</span></label>
              <input
                type="number"
                id="numeroPessoas"
                className={`input ${formErrors.numeroPessoas ? 'input-error' : ''}`}
                value={formData.numeroPessoas}
                onChange={e => onChange(prev => ({ ...prev, numeroPessoas: Number(e.target.value) || 1 }))}
                min={1}
                max={20}
                required
              />
              {formErrors.numeroPessoas && <p className="error-text">{formErrors.numeroPessoas}</p>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="valor" className="label"><Euro className="w-4 h-4 inline mr-1"/>Valor (€)</label>
              <input
                type="number"
                id="valor"
                className="input"
                value={formData.valor ?? ''}
                onChange={e => onChange(prev => ({ ...prev, valor: e.target.value ? Number(e.target.value) : undefined }))}
                min={0}
                step={0.01}
                placeholder="0.00"
              />
            </div>
            <div className="form-group">
              <label htmlFor="canalId" className="label"><Radio className="w-4 h-4 inline mr-1"/>Canal</label>
              <select
                id="canalId"
                className="input"
                value={formData.canalId ?? ''}
                onChange={e => onChange(prev => ({ ...prev, canalId: e.target.value ? Number(e.target.value) : undefined }))}
              >
                <option value="">Auto (default)</option>
                {canais.map(c => <option key={c.id} value={c.id}>{c.descricao}{c.isDefault?' (default)':''}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="notasReserva" className="label">Notas</label>
            <textarea
              id="notasReserva"
              className="input"
              value={formData.notasReserva || ''}
              onChange={e => onChange(prev => ({ ...prev, notasReserva: e.target.value }))}
              rows={3}
              placeholder="Observações sobre a reserva..."
            />
          </div>
        </form>

        <footer className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancelar
          </button>

          {reservaEditando && onConfirmar && (
            <button
              type="button"
              className="btn btn-success"
              onClick={() => onConfirmar(reservaEditando)}
              disabled={loading}
            >
              <CheckCircle className="w-4 h-4" />
              Confirmar
            </button>
          )}

          {reservaEditando && onCancelar && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => onCancelar(reservaEditando)}
              disabled={loading}
            >
              <XCircle className="w-4 h-4" />
              Cancelar reserva
            </button>
          )}

          <button
            type="submit"
            form="reserva-form"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>A guardar...</span>
              </>
            ) : (
              reservaEditando ? 'Atualizar' : 'Criar'
            )}
          </button>
        </footer>
      </div>
    </div>
  );
}
